"use client";

import { cn } from "@/lib/utils";
import { normalizeCmsHtml } from "@/lib/cmsHtml";
import RevealOnScroll from "@/components/atoms/RevealOnScroll";
import { FC, useMemo } from "react";
import { z } from "zod";

/**
 * Coerce API/DB booleans (0/1, "true"/"false") — aligned with loose CMS payloads.
 */
function boolDefault(defaultValue: boolean) {
  return z
    .union([z.boolean(), z.number(), z.string()])
    .optional()
    .transform((v) => {
      if (v === undefined || v === null) return defaultValue;
      if (typeof v === "boolean") return v;
      if (typeof v === "number") return v !== 0;
      const s = String(v).toLowerCase();
      if (s === "false" || s === "0") return false;
      if (s === "true" || s === "1") return true;
      return defaultValue;
    });
}

/** Footer item — aligned with `footer_3` table fields + `IFooterItem`. */
const Footer3ItemSchema = z.object({
  id: z.union([z.number(), z.string()]).optional(),
  label: z.string().optional().default(""),
  slug: z.string().optional().default(""),
  label_color: z.string().optional().default(""),
  url: z.string().optional().default(""),
  description: z.string().optional().default(""),
  icon_url: z.string().optional().default(""),
  icon_alt: z.string().optional().default(""),
  priority: z.coerce.number().optional().default(0),
});

type Footer3Item = z.infer<typeof Footer3ItemSchema>;

const Footer3CategorySchema = z
  .object({
    id: z.union([z.number(), z.string()]).optional(),
    name: z.string(),
    name_text_color: z.string().optional().default(""),
    order_index: z.coerce.number().optional().default(0),
    column_index: z.coerce.number().optional().nullable(),
    status: boolDefault(true),
    items: z.array(Footer3ItemSchema).optional().default([]),
  })
  .transform((cat) => ({
    ...cat,
    items: [...(cat.items ?? [])].sort(
      (a, b) => (a.priority ?? 0) - (b.priority ?? 0)
    ),
  }));

type Footer3Category = z.output<typeof Footer3CategorySchema>;

/** Section payload — aligned with `IFooter` / footer variant 3 CMS fields. */
const Footer3DataSchema = z
  .object({
    id: z.union([z.number(), z.string()]).optional(),
    page_id: z.coerce.number().optional(),
    component_id: z.coerce.number().optional(),
    title: z.string().optional().default(""),
    title_text_color: z.string().optional().default(""),
    background_color: z.string().optional().default("#1a1a2e"),
    text_color: z.string().optional().default("#FFFFFF"),
    enable_footer_content: boolDefault(true),
    enable_copyright: boolDefault(true),
    enable_animation: boolDefault(false),
    copyright_text: z.string().optional().default(""),
    copyright_text_color: z.string().optional().default("#FFFFFF"),
    column_count: z.coerce.number().optional().nullable(),
    variant: z.string().optional().default("3"),
    status: boolDefault(true),
    categories: z.array(Footer3CategorySchema).optional().default([]),
  })
  .transform((data) => ({
    ...data,
    categories: (data.categories ?? [])
      .filter((c) => c.status)
      .sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0)),
  }));

type Footer3Data = z.output<typeof Footer3DataSchema>;

/** Max CMS columns per row from sm breakpoint up (xs stacks). */
const DESKTOP_MAX_COLS_PER_ROW = 3;

/**
 * Partner / social logo raster — max height × max width per breakpoint (Atlas footer Figma).
 * Img `width` / `height` use xl caps for layout hint; CSS clamps display size.
 */
const FOOTER3_ICON_IMG_WIDTH = 128;
const FOOTER3_ICON_IMG_HEIGHT = 32;

/** Figma 4731:11387 — border under category title (wraps 4731:11388 “Academics”) */
const FOOTER3_CATEGORY_UNDERLINE_COLOR = "rgba(109, 119, 122, 0.4)";

function chunkColumns<T>(arr: T[], size: number): T[][] {
  if (size < 1) return [arr];
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    out.push(arr.slice(i, i + size));
  }
  return out;
}

function renderColumnStack(
  colCats: Footer3Category[],
  colKey: string,
  defaultTextColor: string,
  isAnimationEnabled: boolean
) {
  return (
    <div
      key={colKey}
      className={cn(
        "flex min-w-0 flex-col",
        /* Mobile: center column contents; sm+: stretch to grid cell */
        "items-center sm:items-stretch",
        /* Column max width — Atlas footer "Program & Admission" (Figma 4275:17730, 4235:17558, 4210:17387, 4235:17472) */
        "sm:max-w-[280px] md:max-w-[303.33px] lg:max-w-[356.67px] xl:max-w-[423.33px]",
        /* Category blocks within one CMS column — Figma vertical rhythm */
        "gap-8 sm:gap-10 md:gap-10 lg:gap-12 xl:gap-12"
      )}
    >
      {colCats.map((cat) => (
        <CategoryBlock
          key={String(cat.id ?? cat.name)}
          cat={cat}
          defaultTextColor={defaultTextColor}
          isAnimationEnabled={isAnimationEnabled}
        />
      ))}
    </div>
  );
}

function sortByPriority(a: Footer3Item, b: Footer3Item) {
  return (a.priority ?? 0) - (b.priority ?? 0);
}

function partitionFooterItems(items: Footer3Item[] | undefined) {
  const list = [...(items ?? [])].sort(sortByPriority);
  const withIcon: Footer3Item[] = [];
  const textOnly: Footer3Item[] = [];
  for (const item of list) {
    if (item.icon_url?.trim()) withIcon.push(item);
    else textOnly.push(item);
  }
  return { withIcon, textOnly };
}

function CategoryBlock({
  cat,
  defaultTextColor,
  isAnimationEnabled,
}: {
  cat: Footer3Category;
  defaultTextColor: string;
  isAnimationEnabled: boolean;
}) {
  const titleColor = cat.name_text_color?.trim() || defaultTextColor;
  const { withIcon, textOnly } = partitionFooterItems(cat.items);

  return (
    <div className="min-w-0 w-full max-w-full text-center sm:text-left">
      {/* Category title — hugging underline (Figma 4731:11388 / frame 11387: text-width box); underline follows glyphs */}
      <RevealOnScroll enableAnimation={isAnimationEnabled}>
        <h3
          className={cn(
            "m-0 inline-block w-fit max-w-full font-bold leading-120 underline decoration-solid",
            /* Merriweather Bold — per breakpoint (Figma Atlas footer, e.g. 4731-11352…11367) */
            "text-[16px] tracking-[0.16px]",
            "sm:text-[17px] sm:tracking-[0.17px]",
            "md:text-[18px] md:tracking-[0.18px]",
            "lg:text-[20px] lg:tracking-[0.2px]",
            "xl:text-[20px] xl:tracking-[0.2px]",
            /* Offset + thickness — Figma footer heading (4731:11388) */
            "underline-offset-[3px] lg:underline-offset-[4px]",
            "[text-decoration-thickness:1px] lg:[text-decoration-thickness:2px]"
          )}
          style={{
            color: titleColor,
            textDecorationColor: FOOTER3_CATEGORY_UNDERLINE_COLOR,
            fontFamily: "var(--font-merriweather)",
          }}
        >
          {cat.name}
        </h3>
      </RevealOnScroll>

      {/* Space from underline to icons / links */}
      <div
        className={cn(
          "shrink-0",
          "h-4 sm:h-5 md:h-5 lg:h-6 xl:h-6"
        )}
        aria-hidden
      />

      {withIcon.length > 0 ? (
        <RevealOnScroll enableAnimation={isAnimationEnabled}>
          <ul
            className={cn(
              "m-0 flex list-none flex-wrap items-center justify-center p-0 sm:justify-start",
              /* Social / icon row — gaps per breakpoint (Figma 4731-11349) */
              "gap-x-4 gap-y-2.5 sm:gap-x-5 sm:gap-y-3 md:gap-x-5 md:gap-y-3 lg:gap-x-5 lg:gap-y-3 xl:gap-x-5 xl:gap-y-3",
              textOnly.length > 0 &&
              "mb-4 sm:mb-5 md:mb-5 lg:mb-6 xl:mb-6"
            )}
          >
            {withIcon.map((item) => {
              const href = item.url?.trim() || "#";
              const alt =
                item.icon_alt?.trim() || item.label?.trim() || "Social link";
              const descHtml = item.description?.trim();
              return (
                <li
                  key={String(item.id ?? `${href}-${alt}`)}
                  className={cn(
                    "m-0 flex flex-col p-0",
                    "items-center sm:items-start",
                    descHtml && "gap-1"
                  )}
                >
                  <a
                    href={href}
                    className="inline-flex items-center justify-center transition-opacity hover:opacity-80 focus-visible:outline-2 focus-visible:outline-offset-2"
                    style={{
                      color: item.label_color || defaultTextColor,
                      fontFamily: "var(--font-google-sans)",
                      outlineColor: defaultTextColor,
                    }}
                    aria-label={alt}
                  >
                    <img
                      src={item.icon_url!.trim()}
                      alt={alt}
                      width={FOOTER3_ICON_IMG_WIDTH}
                      height={FOOTER3_ICON_IMG_HEIGHT}
                      className={cn(
                        "w-auto object-contain object-center sm:object-left",
                        "h-5 max-h-5 max-w-[80px]",
                        "sm:h-6 sm:max-h-6 sm:max-w-[100px]",
                        "md:h-[26px] md:max-h-[26px] md:max-w-[112px]",
                        "lg:h-7 lg:max-h-7 lg:max-w-[120px]",
                        "xl:h-8 xl:max-h-8 xl:max-w-[128px]"
                      )}
                      loading="lazy"
                      decoding="async"
                    />
                  </a>
                  {descHtml ? (
                    <div
                      className={cn(
                        "max-w-full text-center text-[12px] leading-140 sm:text-left",
                        "[&_a]:underline-offset-[3px]"
                      )}
                      style={{
                        color: item.label_color || defaultTextColor,
                        fontFamily: "var(--font-wf-visual-sans)",
                      }}
                      dangerouslySetInnerHTML={{
                        __html: normalizeCmsHtml(descHtml),
                      }}
                    />
                  ) : null}
                </li>
              );
            })}
          </ul>
        </RevealOnScroll>
      ) : null}

      {textOnly.length > 0 ? (
        <ul
          className={cn(
            "m-0 flex list-none flex-col items-center p-0 sm:items-start",
            /* Text link stack — Figma list gaps */
            "gap-[10px] sm:gap-3 md:gap-[14px] lg:gap-4 xl:gap-4"
          )}
        >
          {textOnly.map((item) => {
            const descHtml = item.description?.trim();
            return (
              <li
                key={String(item.id ?? item.label)}
                className={cn(
                  "m-0 flex flex-col p-0",
                  "items-center sm:items-start",
                  "text-center sm:text-left",
                  descHtml && "gap-1"
                )}
              >
                <a
                  href={item.url?.trim() || "#"}
                  className={cn(
                    "inline font-medium leading-140 transition-opacity hover:opacity-80",
                    "text-[12px] -tracking-012",
                    "sm:text-[13px] sm:-tracking-[0.13px]",
                    "md:text-[14px] md:-tracking-014",
                    "lg:text-[14px] lg:-tracking-014",
                    "xl:text-[15px] xl:-tracking-[0.15px]",
                    "underline-offset-[3px] hover:underline"
                  )}
                  style={{
                    color: item.label_color || defaultTextColor,
                    fontFamily: "var(--font-google-sans)",
                  }}
                >
                  {item.label}
                </a>
                {descHtml ? (
                  <div
                    className={cn(
                      "max-w-full text-[12px] leading-140 sm:text-[13px] md:text-[14px] lg:text-[14px] xl:text-[15px]",
                      "[&_a]:underline-offset-[3px]"
                    )}
                    style={{
                      color: item.label_color || defaultTextColor,
                      fontFamily: "var(--font-google-sans)",
                    }}
                    dangerouslySetInnerHTML={{
                      __html: normalizeCmsHtml(descHtml),
                    }}
                  />
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}

export const Footer3: FC<any> = (props) => {
  const actualData = props?.data?.data ?? props?.data ?? props;

  const parsedResult = useMemo(
    () => Footer3DataSchema.safeParse(actualData),
    [actualData]
  );

  if (!parsedResult.success) {
    console.error("Footer3 validation failed", parsedResult.error);
    return null;
  }

  const sectionData: Footer3Data = parsedResult.data;

  if (!sectionData.status) return null;

  const {
    background_color,
    text_color,
    copyright_text,
    copyright_text_color,
    enable_copyright,
    enable_footer_content,
    enable_animation,
    column_count,
    categories,
  } = sectionData;

  const isAnimationEnabled = enable_animation;

  const colsRaw = Number(column_count);
  const cols =
    Number.isFinite(colsRaw) && colsRaw >= 1 ? Math.min(12, Math.floor(colsRaw)) : 1;

  const columns = useMemo(() => {
    const byCol: Footer3Category[][] = Array.from({ length: cols }, () => []);
    for (const cat of categories) {
      const oneBased = Number(cat.column_index);
      const slot =
        Number.isFinite(oneBased) && oneBased >= 1
          ? Math.min(oneBased, cols) - 1
          : 0;
      byCol[slot].push(cat);
    }
    return byCol;
  }, [categories, cols]);

  const desktopRows = useMemo(
    () => chunkColumns(columns, DESKTOP_MAX_COLS_PER_ROW),
    [columns]
  );

  return (
    <footer className="w-full" style={{ color: text_color }}>
      {enable_footer_content ? (
        <div
          className={cn(
            "relative flex w-full flex-col items-center overflow-x-hidden",
            /* Main section padding — same scale as ProgramBenefits2 */
            "xs:px-[24px] xs:pt-[40px]",
            "sm:px-[60px] sm:pt-[40px]",
            "md:px-[80px] md:pt-[60px]",
            "lg:px-[80px] lg:pt-[60px]",
            "xl:px-[140px] xl:pt-[80px]"
          )}
          style={{ backgroundColor: background_color }}
        >
          <div
            className={cn(
              "relative z-10 w-full",
              /* Inner content widths — ProgramBenefits2 */
              "mx-auto max-w-[335px] sm:mx-0 sm:max-w-none",
              "w-full sm:w-[960px] md:w-[1070px] lg:w-[1280px] xl:w-[1470px]"
            )}
          >
            {/* xs only (below sm): stack CMS columns, all content centered */}
            <div
              className={cn(
                "flex w-full flex-col items-center text-center sm:hidden",
                "gap-8"
              )}
            >
              {columns.map((colCats, i) =>
                renderColumnStack(colCats, `m-${i}`, text_color, isAnimationEnabled)
              )}
            </div>

            {/* sm and up: max 3 CMS columns per row */}
            <div
              className={cn(
                "hidden w-full flex-col sm:flex",
                "gap-10 sm:gap-10 md:gap-10 lg:gap-12 xl:gap-12"
              )}
            >
              {desktopRows.map((row, rowIdx) => (
                <div
                  key={`row-${rowIdx}`}
                  className={cn(
                    "mx-auto grid w-full",
                    /* 1–2 CMS columns: narrow grid, centered in the inner width; 3 cols: full row */
                    "gap-x-8 sm:gap-x-10 lg:gap-x-12 xl:gap-x-12"
                  )}
                  style={{
                    gridTemplateColumns: `repeat(${row.length}, minmax(0, 1fr))`,
                  }}
                >
                  {row.map((colCats, i) =>
                    renderColumnStack(
                      colCats,
                      `d-${rowIdx}-${i}`,
                      text_color,
                      isAnimationEnabled
                    )
                  )}
                </div>
              ))}
            </div>

            {enable_copyright && copyright_text ? (
              <div
                className={cn(
                  "flex w-full justify-center border-t border-[#5B789F]",
                  /* Gap above copyright — Atlas footer Figma (xs–xl); outer shell already handles horizontal padding */
                  "mt-[24px] sm:mt-[28px] md:mt-[32px] lg:mt-[40px] xl:mt-[48px]",
                  /* Vertical padding inside copyright row */
                  "py-[16px] sm:py-[18px] md:py-[20px] lg:py-[22px] xl:py-[24px]"
                )}
                style={{ backgroundColor: background_color }}
              >
                <div
                  className={cn(
                    "m-0 max-w-full text-center font-semibold",
                    "text-[12px] leading-[1.5] -tracking-012",
                    "sm:text-[14px] sm:leading-normal sm:-tracking-014",
                    "lg:text-[16px] lg:-tracking-016"
                  )}
                  style={{
                    color: copyright_text_color,
                    fontFamily: "var(--font-google-sans)",
                  }}
                  dangerouslySetInnerHTML={{
                    __html: normalizeCmsHtml(copyright_text),
                  }}
                />
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </footer>
  );
};

export default Footer3;
